import axios from 'axios';

export const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor para adicionar token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  response => response,
  error => {
    // 401 no PRÓPRIO login é senha errada: recarregar a página apagava a
    // mensagem de erro antes de a tela mostrá-la.
    const ehLogin = /\/auth\/login/.test(error.config?.url || '');
    if (error.response?.status === 401 && !ehLogin) {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Axios SEM baseURL, mas com o login do escritório. Para as telas que montam a
// URL inteira (`${API_BASE}/...`) com o axios cru: sem isto elas iam sem token e
// voltavam 401 (Saúde dos crons, Motor NF, Certidões, Métricas da ANA, Folha 12m).
export const axiosComToken = axios.create();
axiosComToken.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !(config.headers && config.headers.Authorization)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// === AUTH ===
export const authApi = {
  loginEscritorio: (email, senha) =>
    api.post('/auth/login/escritorio', { email, senha }),
  loginEscritorioMfa: (mfa_session_token, codigo) =>
    api.post('/auth/login/escritorio/mfa', { mfa_session_token, codigo }),
  mfaStatus: () => api.get('/auth/mfa/status'),
  mfaIniciar: () => api.post('/auth/mfa/iniciar'),
  mfaConfirmar: (codigo) => api.post('/auth/mfa/confirmar', { codigo }),
  mfaDesativar: (codigo) => api.post('/auth/mfa/desativar', { codigo }),
  trocarSenha: (senha_atual, senha_nova) =>
    api.post('/auth/trocar-senha', { senha_atual, senha_nova }),
  listarUsuariosEscritorio: () => api.get('/auth/escritorio/usuarios'),
  criarUsuarioEscritorio: (dados) => api.post('/auth/escritorio/usuarios', dados),
  desativarUsuarioEscritorio: (id) => api.post(`/auth/escritorio/usuarios/${id}/desativar`),
  forcarTrocarSenha: (id) => api.post(`/auth/escritorio/usuarios/${id}/forcar-trocar-senha`),
};

export const auditApi = {
  listar: (filtros = {}) => {
    const p = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) p.set(k, v); });
    return api.get(`/audit?${p}`);
  },
};

// === CONTRATOS (restrito por e-mail — só quem está em CONTRATO_USUARIOS_AUTORIZADOS) ===
export const contratosApi = {
  acesso: () => api.get('/contratos/acesso'),          // { permitido }
  listar: (params) => api.get('/contratos', { params }),
  obter: (id) => api.get(`/contratos/${id}`),
  criar: (dados) => api.post('/contratos', dados),      // { id }
  pdfUrl: (id) => `${API_BASE}/contratos/${id}/pdf?token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
};

// === CLIENTES ===
export const clientesApi = {
  listar: () => api.get('/clientes'),
  buscar: (id) => api.get(`/clientes/${id}`),
  criar: (dados) => api.post('/clientes', dados),
  atualizar: (id, dados) => api.put(`/clientes/${id}`, dados),
  exclusaoPrevia: (id) => api.get(`/clientes/${id}/exclusao/previa`),
  excluir: (id, confirmar) => api.delete(`/clientes/${id}`, { data: { confirmar } }),
  importar: (clientes, senha_padrao) => api.post('/clientes/importar', { clientes, senha_padrao }),
  // Revisão da 1ª nota (ensaio na produção restrita, encerra o chamado se passar).
  revisarEmissao: (id) => api.post(`/clientes/${id}/emissao/revisar`),
  revisaoEmissao: (id) => api.get(`/clientes/${id}/emissao/revisao`),
};

// === TOMADORES ===
export const tomadoresApi = {
  listar: (clienteId) => api.get(`/clientes/${clienteId}/tomadores`),
  // Pré-cadastra os tomadores recorrentes do cliente a partir das notas REAIS dele no
  // Portal Nacional (ADN → Receita). Idempotente; chamado em background ao escolher o
  // cliente no cockpit. Devolve { importados, ja_existentes, sem_nome_cpf, ... }.
  importarRecorrentes: (clienteId) => api.post(`/clientes/${clienteId}/tomadores/importar-recorrentes`),
  buscar: (id) => api.get(`/tomadores/${id}`),
  criar: (clienteId, dados) => api.post(`/clientes/${clienteId}/tomadores`, dados),
  atualizar: (id, dados) => api.put(`/tomadores/${id}`, dados),
  remover: (id) => api.delete(`/tomadores/${id}`),
  // Autofill no card de emissão: puxa razão social + endereço + IBGE da Receita/ViaCEP
  // (read-only, fail-open → { encontrado:false }).
  lookupCnpj: (cnpj) => api.get(`/tomadores/lookup/cnpj/${String(cnpj).replace(/\D/g, '')}`),
  lookupCep: (cep) => api.get(`/tomadores/lookup/cep/${String(cep).replace(/\D/g, '')}`),
};

// === NOTAS FISCAIS ===
export const notasFiscaisApi = {
  listar: (params) => api.get('/notas-fiscais', { params }),
  // Controle de Notas: clientes → meses → notas do mês (serviço + produto). Só leitura.
  controle: (params) => api.get('/notas-fiscais/controle', { params }),
  // Controle Fiscal integrado: uma linha por empresa, por competência. Só leitura.
  controleFiscal: (params) => api.get('/controle-fiscal', { params }),
  controleFiscalManual: (dados) => api.put('/controle-fiscal/manual', dados),
  controleFiscalManualRemover: (dados) => api.delete('/controle-fiscal/manual', { data: dados }),
  buscar: (id) => api.get(`/notas-fiscais/${id}`),
  criar: (dados) => api.post('/notas-fiscais', dados),
  // Preview read-only do motor de retenção (Fase 3) — calcula CSRF/IRRF/INSS sem gravar.
  previewRetencao: (dados) => api.post('/notas-fiscais/retencao/preview', dados),
  // Autofill do histórico: última config de serviço (cTribNac/NBS/alíquota) do cliente,
  // preferindo o par cliente+tomador. READ-ONLY (não grava nada).
  ultimaConfig: (clienteId, tomadorId) => api.get('/notas-fiscais/ultima-config', { params: { cliente_id: clienteId, tomador_id: tomadorId || undefined } }),
  // Alíquota do ISS que o município aplica ao código (não optante). READ-ONLY.
  aliquotaMunicipalOpcoes: (clienteId, codigoServico, competencia) => api.get('/notas-fiscais/aliquota-municipal-opcoes', { params: { cliente_id: clienteId, codigo_servico: codigoServico, competencia: competencia || undefined } }),
  aliquotaMunicipal: (clienteId, codigoServico, competencia) => api.get('/notas-fiscais/aliquota-municipal', { params: { cliente_id: clienteId, codigo_servico: codigoServico, competencia: competencia || undefined } }),
  aprovar: (id) => api.put(`/notas-fiscais/${id}/aprovar`),
  rejeitar: (id, motivo) => api.put(`/notas-fiscais/${id}/rejeitar`, { motivo }),
  emitir: (id, body) => api.put(`/notas-fiscais/${id}/emitir`, body || {}),
  cancelar: (id, motivo) => api.put(`/notas-fiscais/${id}/cancelar`, { motivo }),
  // Rascunhos (Sprint A+B 2026-05-19) — Ana cria draft em modo equipe,
  // operador revisa no painel e emite via UI estruturada
  atualizarRascunho: (id, dados) => api.put(`/notas-fiscais/${id}/draft`, dados),
  emitirRascunho: (id) => api.post(`/notas-fiscais/${id}/emitir-rascunho`),
  descartarRascunho: (id) => api.delete(`/notas-fiscais/${id}/draft`),
  // Importar planilha de recebimentos → cria RASCUNHOS (nunca emite). Preview é read-only.
  importarPlanilhaPreview: (clienteId, arquivo) => {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('cliente_id', clienteId);
    return api.post('/notas-fiscais/importar-planilha/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importarPlanilhaCriar: (clienteId, descricao, arquivo) => {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('cliente_id', clienteId);
    fd.append('descricao', descricao);
    return api.post('/notas-fiscais/importar-planilha/criar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  resumo: () => api.get('/notas-fiscais/dashboard/resumo'),
  enviarEmail: (id, dados) => api.post(`/notas-fiscais/${id}/enviar-email`, dados),
  emailStatus: () => api.get('/notas-fiscais/email/status'),
  relatorioFaturamento: (params) => api.get('/notas-fiscais/relatorios/faturamento', { params }),
  relatorioStatus: (params) => api.get('/notas-fiscais/relatorios/status', { params }),
  relatorioRankingTomadores: (params) => api.get('/notas-fiscais/relatorios/ranking-tomadores', { params }),
  // Declaração de Faturamento (12m Simples) assinada com e-CPF ICP-Brasil do Thiago.
  // Gera + assina sob demanda — pode levar ~10-30s (busca extrato no SERPRO).
  gerarDeclaracaoFaturamento: (cnpj) => api.get(`/notas-fiscais/declaracao-faturamento/${cnpj}`),
};

// === DOCUMENTOS GERADOS (certidão / declaração — async + arquivados) ===
// Geração ASSÍNCRONA: enfileira (volta na hora, sem travar carregando), faz
// polling em status() e baixa o PDF como blob. listar() é o histórico de
// controle por cliente (com status do arquivamento no Drive).
export const documentosApi = {
  gerarCertidao: (clienteId) => api.post('/documentos/certidao', { cliente_id: clienteId }),
  // SÓ a situação fiscal (SITFIS do eCAC), sem tentar tirar a certidão. Mesmo
  // job/polling/preview da certidão (documentosApi.status + baixarPdf).
  gerarSitfis: (clienteId) => api.post('/documentos/sitfis', { cliente_id: clienteId }),
  gerarDeclaracao: (clienteId) => api.post('/documentos/declaracao', { cliente_id: clienteId }),
  // Declaração MANUAL: operador informa os 12 meses à mão (números aproximados).
  // payload = { cliente_id, linhas:[{mes,ano,valor}], assinar }. assinar default true (e-CPF).
  gerarDeclaracaoManual: (payload) => api.post('/documentos/declaracao/manual', payload),
  // Lucro Presumido: detalhamento dos 12 meses (origem por mês) + meses pré-migração
  // sem faturamento informado (gaps). Pro Simples vem vazio.
  precheckDeclaracao: (clienteId) => api.get('/documentos/declaracao/precheck', { params: { cliente_id: clienteId } }),
  // Informa o faturamento de um mês pré-migração; devolve o precheck atualizado.
  salvarComplemento: (payload) => api.post('/documentos/declaracao/complemento', payload),
  listar: (params) => api.get('/documentos', { params }),
  // Central de Documentos (Drive interno): "pastas" por empresa com a contagem.
  resumo: () => api.get('/documentos/resumo'),
  status: (id) => api.get(`/documentos/${id}`),
  // Baixa o PDF (blob) já autenticado — parte=2 é o resumo de débitos da certidão positiva.
  baixarPdf: (id, parte = 1) => api.get(`/documentos/${id}/pdf`, { params: { parte }, responseType: 'blob' }),
};

// === CERTIFICADOS ===
export const certificadosApi = {
  listarTodos: () => api.get('/certificados'),
  resumoVencimentos: () => api.get('/certificados/vencimentos/resumo'),
  controle: () => api.get('/certificados/controle'),
  anotar: (cliente_id, texto) => api.post('/certificados/controle/anotacao', { cliente_id, texto }),
  anotacoes: (cliente_id) => api.get(`/certificados/controle/anotacoes/${cliente_id}`),
  consultar: (clienteId) => api.get(`/certificados/${clienteId}`),
  upload: (clienteId, file, senha) => {
    const formData = new FormData();
    formData.append('certificado', file);
    formData.append('senha', senha);
    return api.post(`/certificados/${clienteId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  remover: (clienteId) => api.delete(`/certificados/${clienteId}`),
};

// === PROCURAÇÕES NO e-CAC ===
export const procuracoesApi = {
  fila: () => api.get('/procuracoes/fila'),
  historico: (cliente_id) => api.get('/procuracoes', { params: cliente_id ? { cliente_id } : {} }),
  cadastrar: (payload) => api.post('/procuracoes/cadastrar', payload),
};

// === AVISOS E COMUNICADOS AO CLIENTE ===
// A fila e o histórico são de leitura da equipe; criar/editar/cancelar é
// restrito (o backend recusa quem não está na lista — ver podeComunicar).
export const avisosClienteApi = {
  resumo: () => api.get('/notificacoes-cliente/resumo'),
  podeComunicar: () => api.get('/notificacoes-cliente/pode-comunicar'),
  comunicados: (limite = 30) => api.get('/notificacoes-cliente/comunicados', { params: { limite } }),
  comunicado: (id) => api.get(`/notificacoes-cliente/comunicado/${id}`),
  previa: (payload) => api.post('/notificacoes-cliente/comunicado/previa', payload),
  criar: (payload) => api.post('/notificacoes-cliente/comunicado', payload),
  atualizar: (id, payload) => api.put(`/notificacoes-cliente/comunicado/${id}`, payload),
  cancelar: (id) => api.post(`/notificacoes-cliente/comunicado/${id}/cancelar`),
};

// === LICENÇAS & ALVARÁS ===
export const licencasApi = {
  controle: () => api.get('/licencas/controle'),
  criar: (payload) => api.post('/licencas', payload),
  atualizar: (id, payload) => api.put(`/licencas/${id}`, payload),
  remover: (id) => api.delete(`/licencas/${id}`),
  situacao: (id, situacao) => api.post(`/licencas/${id}/situacao`, { situacao }),
  anotar: (id, texto) => api.post(`/licencas/${id}/anotacao`, { texto }),
  anotacoes: (id) => api.get(`/licencas/${id}/anotacoes`),
  simularAviso: () => api.post('/licencas/aviso/simular', {}),
};

// === PROCESSOS NA JUNTA (societário) ===
export const processosSocietariosApi = {
  painel: () => api.get('/processos-societarios/painel'),
  criar: (payload) => api.post('/processos-societarios', payload),
  atualizar: (id, payload) => api.put(`/processos-societarios/${id}`, payload),
  remover: (id) => api.delete(`/processos-societarios/${id}`),
  moverEtapa: (id, etapa, nota) => api.post(`/processos-societarios/${id}/etapa`, { etapa, nota }),
  registrarNota: (id, texto, tipo) => api.post(`/processos-societarios/${id}/nota`, { texto, tipo }),
  historico: (id) => api.get(`/processos-societarios/${id}/historico`),
  simularCobranca: () => api.post('/processos-societarios/cobranca/simular', {}),
  checklist: (id) => api.get(`/processos-societarios/${id}/checklist`),
  addChecklistItem: (id, texto) => api.post(`/processos-societarios/${id}/checklist`, { texto }),
  marcarChecklist: (itemId, concluido) => api.post(`/processos-societarios/checklist/${itemId}/marcar`, { concluido }),
  removerChecklist: (itemId) => api.delete(`/processos-societarios/checklist/${itemId}`),
  minuta: (id) => api.get(`/processos-societarios/${id}/minuta`),
};

// === IA / AGENTE ===
export const iaApi = {
  creditos: () => api.get('/whatsapp/agente/creditos'),
};

// === PAINEL OPERACIONAL ===
export const painelApi = {
  operacoesHoje: () => api.get('/painel/operacoes-hoje'),
  listarFila: (status = 'pendente', limit = 50) =>
    api.get('/painel/fila-aprovacao', { params: { status, limit } }),
  buscarFila: (id) => api.get(`/painel/fila-aprovacao/${id}`),
  aprovar: (id, observacao) => api.post(`/painel/fila-aprovacao/${id}/aprovar`, { observacao }),
  rejeitar: (id, motivo) => api.post(`/painel/fila-aprovacao/${id}/rejeitar`, { motivo }),
  criarPendenciaTeste: (dados) => api.post('/painel/fila-aprovacao', dados),
};

// === INTEGRA CONTADOR (SERPRO) ===
export const integraContadorApi = {
  status: () => api.get('/integra-contador/status'),
  testarAutenticacao: () => api.post('/integra-contador/autenticar/teste'),
  consultarUltimaPgdasd: (cnpj) => api.get(`/integra-contador/pgdasd/ultima-declaracao/${cnpj}`),
  consultarProcuracoes: (cnpj) => api.get(`/integra-contador/procuracoes/${cnpj}`),
  consultarDctfweb: (cnpj) => api.get(`/integra-contador/dctfweb/${cnpj}`),
  caixaPostal: (cnpj) => api.get(`/integra-contador/caixa-postal/${cnpj}`),
  // DAS
  gerarDasSimples:         (cnpj, periodoApuracao) => api.post('/integra-contador/das/simples',         { cnpj, periodoApuracao }),
  gerarDasSimplesCobranca: (cnpj, periodoApuracao) => api.post('/integra-contador/das/simples/cobranca',{ cnpj, periodoApuracao }),
  gerarDasMei:             (cnpj, periodoApuracao) => api.post('/integra-contador/das/mei',             { cnpj, periodoApuracao }),
  // SITFIS (Certidao)
  obterSitfis: (cnpj) => api.get(`/integra-contador/sitfis/${cnpj}`),
  // Certidao em cascata: tenta a CND federal de verdade; se positiva de debitos,
  // devolve Situacao Fiscal (SITFIS) + Resumo de Debitos juntos. Pode levar alguns
  // minutos (CND faz polling; SITFIS ~2-3min no fallback).
  obterCertidao: (cnpj) => api.get(`/integra-contador/certidao/${cnpj}`),
  // DARF via Sicalc
  gerarDarf: (cnpj, dados) => api.post('/integra-contador/darf', { cnpj, dados }),
  // Guia DCTFWeb
  gerarGuiaDctfweb: (cnpj, dados) => api.post('/integra-contador/dctfweb/guia', { cnpj, dados }),
  // Pagamentos
  consultarPagamentos: (cnpj, dataInicio, dataFim) =>
    api.get(`/integra-contador/pagamentos/${cnpj}`, { params: { dataInicio, dataFim } }),
  // CCMEI
  emitirCcmei: (cnpj) => api.get(`/integra-contador/ccmei/${cnpj}`),
  // Snapshot de obrigacoes (alimentado pelo worker diario)
  snapshotTodos: () => api.get('/integra-contador/snapshot'),
  snapshotCliente: (clienteId) => api.get(`/integra-contador/snapshot/${clienteId}`),
  rodarSnapshot: () => api.post('/integra-contador/snapshot/rodar'),
};

// === APURAÇÃO TRIBUTÁRIA ===
export const apuracaoApi = {
  metadados: () => api.get('/apuracao/metadados'),
  simular: (dados) => api.post('/apuracao/simular', dados),
  enviarFila: (dados) => api.post('/apuracao/enviar-fila', dados),
};

// === ENTREGAS MENSAIS ===
export const entregasApi = {
  dashboard: (competencia) => api.get('/entregas/dashboard', { params: competencia ? { competencia } : {} }),
  atualizarStatus: (dados) => api.post('/entregas/status', dados),
  vencimentosDas: () => api.get('/entregas/vencimentos-das'),
  worklist: () => api.get('/entregas/worklist'),
};



// === DOMINIO (Thomson Reuters / Onvio) ===
export const dominioApi = {
  status: () => api.get('/dominio/status'),
  testarAutenticacao: () => api.post('/dominio/autenticar/teste'),
  verificarAtivacao: (clienteId) => api.get(`/dominio/clientes/${clienteId}/ativacao`),
  cadastrarKey: (clienteId, integrationKey) => api.post(`/dominio/clientes/${clienteId}/integration-key`, { integrationKey }),
  ativar: (clienteId) => api.post(`/dominio/clientes/${clienteId}/ativar`),
  enviarXml: (clienteId, formData) => api.post(`/dominio/clientes/${clienteId}/enviar-xml`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  consultarLote: (clienteId, loteId) => api.get(`/dominio/clientes/${clienteId}/lote/${loteId}`),
};

// === JOÃO (fila assíncrona + daemon) ===
// === PGDAS-D (fechamento Simples Nacional) ===
export const pgdasdApi = {
  // Sprint 5.20 — preview sem persistir, popula formulario editavel
  preview: (cliente_id, periodo_apuracao) =>
    api.get(`/integra-contador/pgdasd/preview?cliente_id=${cliente_id}&periodo_apuracao=${periodo_apuracao}`),
  // Sprint 5.21 — infere folha 12m via DCTFWeb (caro, demora ~30-60s)
  inferirFolha: (cliente_id, periodo_apuracao) =>
    api.post('/integra-contador/folha/inferir', { cliente_id, periodo_apuracao }),
  calcular: (dados) => api.post('/integra-contador/pgdasd/fechamento/calcular', dados),
  // Apurar+aprovar+transmitir+pós-transmissão em 1 chamada. IRREVERSÍVEL na transmissão SERPRO.
  apurarETransmitir: (dados) => api.post('/integra-contador/pgdasd/fechamento/apurar-e-transmitir', dados),
  listar: (filtros = {}) => {
    const params = new URLSearchParams();
    if (filtros.status) params.set('status', Array.isArray(filtros.status) ? filtros.status.join(',') : filtros.status);
    if (filtros.cliente_id) params.set('cliente_id', filtros.cliente_id);
    if (filtros.periodo) params.set('periodo', filtros.periodo);
    if (filtros.limite) params.set('limite', filtros.limite);
    return api.get(`/integra-contador/pgdasd/fechamento?${params}`);
  },
  obter: (id) => api.get(`/integra-contador/pgdasd/fechamento/${id}`),
  editar: (id, valores) => api.put(`/integra-contador/pgdasd/fechamento/${id}/editar`, valores),
  aprovar: (id) => api.post(`/integra-contador/pgdasd/fechamento/${id}/aprovar`, {}),
  // opts.confirmar_retificar=true: o operador confirmou que quer RETIFICAR (a Receita já
  // tem declaração nesta competência; sem isso o backend devolve 409 'confirmar_retificar').
  transmitir: (id, opts = {}) => api.post(`/integra-contador/pgdasd/fechamento/${id}/transmitir`, opts),
  // Envio sem resposta da Receita: consulta (só leitura) se a declaração chegou.
  conferirTransmissao: (id) => api.post(`/integra-contador/pgdasd/fechamento/${id}/conferir-transmissao`, {}),
  cancelar: (id, motivo) => api.post(`/integra-contador/pgdasd/fechamento/${id}/cancelar`, { motivo }),
  // Retransmitir: reabre um fechamento que FALHOU (failed) -> pending_approval, sem
  // criar registro novo. Usado quando corrigimos o payload (ex.: sem-movimento) ou o
  // SERPRO teve indisponibilidade transitória. Depois disso o operador transmite de novo.
  reabrir: (id, motivo) => api.post(`/integra-contador/pgdasd/fechamento/${id}/reabrir`, { motivo }),
  // Retificação: reabre apuração transmitida -> rascunho pra conferir (NÃO re-transmite).
  // modo: 'manual' | 'auto_anexo' (mantém receita/RBT12, re-deriva anexo/Fator R) | 'auto_full' (refaz tudo)
  reabrirRetificar: (id, modo) => api.post(`/integra-contador/pgdasd/fechamento/${id}/reabrir-retificar`, { modo }),
  descartarRetificacao: (id) => api.post(`/integra-contador/pgdasd/fechamento/${id}/descartar-retificacao`, {}),
};

export const joaoApi = {
  status: () => api.get('/joao/status'),
  listarJobs: (filtros = {}) => {
    const params = new URLSearchParams();
    if (filtros.status) params.set('status', Array.isArray(filtros.status) ? filtros.status.join(',') : filtros.status);
    if (filtros.tipo) params.set('tipo', Array.isArray(filtros.tipo) ? filtros.tipo.join(',') : filtros.tipo);
    if (filtros.cliente_id) params.set('cliente_id', filtros.cliente_id);
    if (filtros.limite) params.set('limite', filtros.limite);
    if (filtros.offset) params.set('offset', filtros.offset);
    return api.get(`/joao/jobs?${params}`);
  },
  obterJob: (id) => api.get(`/joao/jobs/${id}`),
  criarJob: (dados) => api.post('/joao/jobs', dados),
  aprovarJob: (id) => api.post(`/joao/jobs/${id}/aprovar`, {}),
  cancelarJob: (id, motivo) => api.post(`/joao/jobs/${id}/cancelar`, { motivo }),
  syncHistorico: (limite = 20) => api.get(`/joao/sync/historico?limite=${limite}`),
  onvioMonitorados: (incluirInativos = false) =>
    api.get(`/joao/onvio-monitored${incluirInativos ? '?todos=1' : ''}`),
};

// === BUG REPORTS (Sprint 1, 2026-05-24) ===
// Equipe submete bugs via cockpit. Sistema cria GitHub Issue + WhatsApp ping pro admin.
// Claude pega via /bugs-novos (label github "bug-equipe") sem o admin precisar traduzir.
export const integracoesApi = {
  status: (force = false) => api.get(`/integracoes/status${force ? '?force=1' : ''}`),
  invalidate: () => api.post('/integracoes/invalidate'),
};

export const driveApi = {
  resumo: () => api.get('/drive/resumo'),
  listar: (filtros = {}) => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
    return api.get(`/drive/arquivos?${params}`);
  },
  arvore: (filtro, clienteId) => api.get('/drive/arvore', { params: { ...(filtro ? { filtro } : {}), ...(clienteId ? { cliente_id: clienteId } : {}) } }),
  arquivosDePasta: (pasta_cliente_nome, subpasta_root) => {
    const p = new URLSearchParams({ pasta_cliente_nome });
    if (subpasta_root) p.set('subpasta_root', subpasta_root);
    return api.get(`/drive/pasta?${p}`);
  },
  syncAgora: () => api.post('/drive/sync'),
  varrerCliente: (clienteId) => api.post(`/drive/clientes/${clienteId}/varrer`, {}, { timeout: 300000 }),
  ignorar: (id) => api.post(`/drive/arquivos/${id}/ignorar`),
  relatorioExtratosPendentes: () => api.get('/drive/relatorios/extratos-pendentes'),
  relatorioExtratosFaltantes: (desde) =>
    api.get(`/drive/relatorios/extratos-faltantes${desde ? `?desde=${desde}` : ''}`),
};

export const dashboardPerformanceApi = {
  evolucao: (dias = 30) => api.get(`/dashboard-performance/evolucao?dias=${dias}`),
  joao: (dias = 30) => api.get(`/dashboard-performance/joao?dias=${dias}`),
  atividadeHoje: (dias = 1) => api.get(`/dashboard-performance/atividade-hoje?dias=${dias}`),
  enviarResumo: () => api.post('/dashboard-performance/enviar-resumo'),
};

export const layoutDominioApi = {
  resumo: () => api.get('/layouts-dominio/resumo'),
  pendentesLayout: (backfill = false) =>
    api.get(`/layouts-dominio/pendentes-layout${backfill ? '?backfill=true' : ''}`),
  prontos: () => api.get('/layouts-dominio/prontos'),
  rodarDiario: (backfill = false) =>
    api.post(`/layouts-dominio/rodar-diario${backfill ? '?backfill=true' : ''}`),
  rodarSemanal: () => api.post('/layouts-dominio/rodar-semanal'),
  marcarImportado: (drive_file_id) =>
    api.post(`/layouts-dominio/${drive_file_id}/marcar-importado`),
  // Sprint 5.19
  filaOrganizada: () => api.get('/layouts-dominio/fila-organizada'),
  execucaoStatus: () => api.get('/layouts-dominio/execucao-status'),
  iniciarJoaoContabilizar: () => api.post('/layouts-dominio/iniciar-joao-contabilizar'),
};

export const pgdasdCorridaApi = {
  resumo: (competencia) => api.get(`/pgdasd-corrida/resumo${competencia ? `?competencia=${competencia}` : ''}`),
  // Painel mensal: todas as empresas do Simples + status da apuração no mês.
  painel: (competencia) => api.get(`/pgdasd-corrida/painel${competencia ? `?competencia=${competencia}` : ''}`),
  elegiveis: () => api.get('/pgdasd-corrida/elegiveis'),
  disparar: (periodo_apuracao, tipo = 'pgdasd-fechar') => api.post('/pgdasd-corrida/disparar', { periodo_apuracao, tipo }),
  // Concilia a competência com o e-CAC (backfill de RBT12/receita/anexo/alíquotas
  // reais). Read-only na SERPRO, NÃO transmite/retifica. competencia: 'YYYY-MM'|'YYYYMM'.
  conciliar: (competencia) => api.post('/pgdasd-corrida/conciliar', { competencia }),
  // Re-puxa o Extrato do Simples (Receita) de TODAS as transmitidas da competência,
  // sobrescrevendo o cache velho. Read-only na SERPRO, NÃO transmite. 'YYYY-MM'|'YYYYMM'.
  refrescarExtratos: (competencia) => api.post('/pgdasd-corrida/extratos-refresh', { competencia }),
  // Re-emite a guia DAS (2ª via cobrança) de TODAS as transmitidas da competência,
  // sobrescrevendo o cache velho e populando o das_numero. NÃO transmite/paga.
  refrescarDas: (competencia) => api.post('/pgdasd-corrida/das-refresh', { competencia }),
  // Devolve uma apuração presa (pendência/falha) pro estado de rascunho ("Rascunho
  // pronto") pra conferir e transmitir na mão. NÃO transmite e NÃO deixa o cron
  // auto-transmitir. cliente_id + competencia ('YYYY-MM'|'YYYYMM').
  voltarRascunho: (cliente_id, competencia) => api.post('/pgdasd-corrida/voltar-rascunho', { cliente_id, competencia }),
};

export const reportsApi = {
  criar: (dados) => api.post('/reports', dados),
  listar: (filtros = {}) => {
    const params = new URLSearchParams();
    if (filtros.status) params.set('status', filtros.status);
    if (filtros.severidade) params.set('severidade', filtros.severidade);
    if (filtros.limit) params.set('limit', filtros.limit);
    return api.get(`/reports?${params}`);
  },
  obter: (id) => api.get(`/reports/${id}`),
  atualizarStatus: (id, status, resolucao_observacao) =>
    api.put(`/reports/${id}/status`, { status, resolucao_observacao }),
  syncGithub: (id) => api.post(`/reports/${id}/sync-github`),
  resumo: () => api.get('/reports/dashboard/resumo'),
};

// === RELATÓRIOS CONTÁBEIS (mensal/trimestral/anual) — READ-ONLY ===
// Simulador da Reforma Tributária (híbrido IBS/CBS × DAS de hoje). READ-ONLY.
// Pontos e bônus mensal dos atendentes (2026-10-22). /meu é de todos (só o próprio);
// o resto é tela restrita da Administração; escrita só admin/manutenção.
export const pontosBonusApi = {
  meu: (competencia) => api.get('/pontos-bonus/meu', { params: { competencia } }),
  mes: (competencia) => api.get('/pontos-bonus/mes', { params: { competencia } }),
  pessoa: (competencia, pessoa) => api.get('/pontos-bonus/pessoa', { params: { competencia, pessoa } }),
  regras: () => api.get('/pontos-bonus/regras'),
  salvarRegras: (patch) => api.put('/pontos-bonus/regras', patch),
  salvarPeso: (categoria, peso) => api.put(`/pontos-bonus/pesos/${encodeURIComponent(categoria)}`, { peso }),
  salarios: () => api.get('/pontos-bonus/salarios'),
  salvarSalario: (pessoa, salario) => api.put('/pontos-bonus/salarios', { pessoa, salario }),
  ajuste: (payload) => api.post('/pontos-bonus/ajuste', payload),
  fechar: (competencia) => api.post('/pontos-bonus/fechar', { competencia }),
  reabrir: (competencia, motivo) => api.post('/pontos-bonus/reabrir', { competencia, motivo }),
  recalcular: (desde) => api.post('/pontos-bonus/recalcular', { desde }),
  fechamentos: () => api.get('/pontos-bonus/fechamentos'),
  auditoria: (params = {}) => api.get('/pontos-bonus/auditoria', { params }),
  auditoriaCsv: (params = {}) => api.get('/pontos-bonus/auditoria.csv', { params, responseType: 'blob' }),
};

export const performanceEquipeApi = {
  // Se o usuário logado pode ver o placar de TODOS (Bruno + Thiago por default)
  // ou só o próprio. A tela pergunta antes de montar o menu.
  permissao: () => api.get('/performance-equipe/permissao'),
  // O PRÓPRIO score. Aberto a todo o escritório; o escopo vem do token, não da URL.
  meu: (params = {}) => api.get('/performance-equipe/meu', { params }),
  // Como cada um atende (sem pontos): tempos por solicitação e o atendimento inteiro.
  comportamento: (params = {}) => api.get('/performance-equipe/comportamento', { params }),
  comportamentoMeu: (params = {}) => api.get('/performance-equipe/comportamento/meu', { params }),
  atendimento: (id, meu = false) => api.get(`/performance-equipe/comportamento/${meu ? 'meu/' : ''}atendimento/${id}`),
  // Daqui pra baixo: restrito por e-mail nominal (403 pra quem não está na lista).
  painel: (params = {}) => api.get('/performance-equipe', { params }),
  config: () => api.get('/performance-equipe/config'),
  salvarConfig: (chave, valor) => api.post('/performance-equipe/config', { chave, valor }),
  vinculos: () => api.get('/performance-equipe/vinculos'),
  salvarVinculo: (nome_planilha, usuario_id) => api.post('/performance-equipe/vinculos', { nome_planilha, usuario_id }),
  salvarMeta: (payload) => api.post('/performance-equipe/meta', payload),
  metaSugerida: (params = {}) => api.get('/performance-equipe/meta-sugerida', { params }),
  // motivo é obrigatório (mín. 10 chars) — o colaborador lê este texto no extrato dele.
  ajustar: (payload) => api.post('/performance-equipe/ajuste', payload),
  // Congela a competência. Exige confirmar:'fechar competencia'.
  fechar: (ano, mes) => api.post('/performance-equipe/fechar', { ano, mes, confirmar: 'fechar competencia' }),
};

export const reformaApi = {
  // Cenários de alíquota (2026 teste / 2027 CBS cheia / 2033 completo) + o que sai do DAS.
  cenarios: () => api.get('/reforma/cenarios'),
  // params: { competencia: 'YYYYMM', percentual_despesas, cenario, cliente_id? }
  simular: (params = {}) => api.get('/reforma/simulacao', { params }),
};

// João · Balancetes (26/09/2026) — extrato → classificação → TXT Domínio + balancete PDF.
// Competência sempre 'AAAA-MM'. Upload é UM arquivo por chamada (a tela manda em fila).
// Módulo contábil (13/10/2026): plano referencial da Receita, abertura de saldos, imobilizado e depreciação.
export const contabilApi = {
  plano: (clienteId = 0, params = {}) => api.get('/contabil/plano', { params: { cliente_id: clienteId, ...params } }),
  planoLegado: (clienteId = 0) => api.get('/contabil/plano/legado', { params: { cliente_id: clienteId } }),
  referencial: (q, params = {}) => api.get('/contabil/plano/referencial', { params: { q, ...params } }),
  referencialConta: (codigo) => api.get(`/contabil/plano/referencial/${encodeURIComponent(codigo)}`),
  mapeamento: (clienteId, competencia) => api.get('/contabil/plano/mapeamento', { params: { cliente_id: clienteId, competencia } }),
  exportarDominio: (clienteId = 0) => api.get('/contabil/plano/exportar-dominio', { params: { cliente_id: clienteId }, responseType: 'blob' }),
  criarSubconta: (dados) => api.post('/contabil/plano/subcontas', dados),
  editarSubconta: (id, dados) => api.patch(`/contabil/plano/subcontas/${id}`, dados),
  excluirSubconta: (id) => api.delete(`/contabil/plano/subcontas/${id}`),
  config: (clienteId, competencia) => api.get(`/contabil/clientes/${clienteId}/config`, { params: { competencia } }),
  definirPlano: (clienteId, dados) => api.put(`/contabil/clientes/${clienteId}/config`, dados),
  // abertura
  aberturaPropor: (clienteId, comp, arquivo) => { const fd = new FormData(); fd.append('arquivo', arquivo); return api.post(`/contabil/clientes/${clienteId}/abertura/${comp}/propor`, fd, { timeout: 300000 }); },
  aberturaProporEcd: (clienteId, comp, arquivo) => { const fd = new FormData(); fd.append('arquivo', arquivo); return api.post(`/contabil/clientes/${clienteId}/abertura/${comp}/propor-ecd`, fd, { timeout: 300000 }); },
  aberturaConfirmar: (clienteId, comp, dados) => api.post(`/contabil/clientes/${clienteId}/abertura/${comp}/confirmar`, dados),
  aberturaDigitada: (clienteId, comp, dados) => api.post(`/contabil/clientes/${clienteId}/abertura/${comp}/digitada`, dados),
  abertura: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/abertura/${comp}`),
  // lançamentos
  lancamentos: (clienteId, params = {}) => api.get(`/contabil/clientes/${clienteId}/lancamentos`, { params }),
  criarLancamento: (clienteId, dados) => api.post(`/contabil/clientes/${clienteId}/lancamentos`, dados),
  estornar: (id, dados = {}) => api.post(`/contabil/lancamentos/${id}/estornar`, dados),
  // imobilizado
  grupos: () => api.get('/contabil/imobilizado/grupos'),
  imobilizadoPainel: (competencia) => api.get('/contabil/imobilizado/painel', { params: { competencia } }),
  bens: (clienteId, params = {}) => api.get(`/contabil/clientes/${clienteId}/imobilizado`, { params }),
  contasDoGrupo: (clienteId, grupo) => api.get(`/contabil/clientes/${clienteId}/imobilizado/contas/${grupo}`),
  criarBem: (clienteId, dados) => api.post(`/contabil/clientes/${clienteId}/imobilizado`, dados),
  bem: (bemId, ate) => api.get(`/contabil/imobilizado/${bemId}`, { params: { ate } }),
  editarBem: (bemId, dados) => api.put(`/contabil/imobilizado/${bemId}`, dados),
  excluirBem: (bemId) => api.delete(`/contabil/imobilizado/${bemId}`),
  baixarBem: (bemId, dados) => api.post(`/contabil/imobilizado/${bemId}/baixa`, dados),
  depreciacaoPrevia: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/depreciacao/${comp}`),
  depreciacaoLancar: (clienteId, comp) => api.post(`/contabil/clientes/${clienteId}/depreciacao/${comp}/lancar`),
  depreciacaoDesfazer: (clienteId, comp, bemId = null) => api.post(`/contabil/clientes/${clienteId}/depreciacao/${comp}/desfazer`, { bem_id: bemId }),
  // históricos, razão, diário, demonstrações, encerramento
  historicos: (clienteId = 0) => api.get('/contabil/historicos', { params: { cliente_id: clienteId } }),
  adicionarHistorico: (clienteId, texto) => api.post('/contabil/historicos', { cliente_id: clienteId, texto }),
  razao: (clienteId, params) => api.get(`/contabil/clientes/${clienteId}/razao`, { params }),
  razaoPdf: (clienteId, params) => api.get(`/contabil/clientes/${clienteId}/razao.pdf`, { params, responseType: 'blob', timeout: 120000 }),
  diario: (clienteId, params) => api.get(`/contabil/clientes/${clienteId}/diario`, { params }),
  diarioPdf: (clienteId, params) => api.get(`/contabil/clientes/${clienteId}/diario.pdf`, { params, responseType: 'blob', timeout: 120000 }),
  dre: (clienteId, comp, acumulado = true) => api.get(`/contabil/clientes/${clienteId}/dre/${comp}`, { params: { acumulado: acumulado ? 1 : 0 } }),
  drePdf: (clienteId, comp, acumulado = true) => api.get(`/contabil/clientes/${clienteId}/dre/${comp}.pdf`, { params: { acumulado: acumulado ? 1 : 0 }, responseType: 'blob', timeout: 120000 }),
  balanco: (clienteId, comp, nivel = 4) => api.get(`/contabil/clientes/${clienteId}/balanco/${comp}`, { params: { nivel } }),
  balancoPdf: (clienteId, comp, nivel = 4) => api.get(`/contabil/clientes/${clienteId}/balanco/${comp}.pdf`, { params: { nivel }, responseType: 'blob', timeout: 120000 }),
  serie: (clienteId, ano) => api.get(`/contabil/clientes/${clienteId}/serie/${ano}`),
  dfc: (clienteId, comp, acumulado = true) => api.get(`/contabil/clientes/${clienteId}/dfc/${comp}`, { params: { acumulado: acumulado ? 1 : 0 } }),
  dfcPdf: (clienteId, comp, acumulado = true) => api.get(`/contabil/clientes/${clienteId}/dfc/${comp}.pdf`, { params: { acumulado: acumulado ? 1 : 0 }, responseType: 'blob', timeout: 120000 }),
  dmpl: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/dmpl/${comp}`),
  dmplPdf: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/dmpl/${comp}.pdf`, { responseType: 'blob', timeout: 120000 }),
  notas: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/notas/${comp}`),
  notasPdf: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/notas/${comp}.pdf`, { responseType: 'blob', timeout: 120000 }),
  contabilizacaoProvisoes: (clienteId, ligar) => api.put(`/contabil/clientes/${clienteId}/contabilizacao`, { provisoes_folha: !!ligar }),
  recorrentes: (clienteId) => api.get(`/contabil/clientes/${clienteId}/recorrentes`),
  criarRecorrente: (clienteId, dados) => api.post(`/contabil/clientes/${clienteId}/recorrentes`, dados),
  editarRecorrente: (rid, dados) => api.put(`/contabil/recorrentes/${rid}`, dados),
  desativarRecorrente: (rid) => api.post(`/contabil/recorrentes/${rid}/desativar`),
  importarPrevia: (clienteId, arquivo) => { const fd = new FormData(); fd.append('arquivo', arquivo); return api.post(`/contabil/clientes/${clienteId}/lancamentos/importar/previa`, fd, { timeout: 120000 }); },
  importarLancamentos: (clienteId, arquivo) => { const fd = new FormData(); fd.append('arquivo', arquivo); return api.post(`/contabil/clientes/${clienteId}/lancamentos/importar`, fd, { timeout: 120000 }); },
  modeloImportacao: (clienteId) => api.get(`/contabil/clientes/${clienteId}/lancamentos/importar/modelo.xlsx`, { responseType: 'blob' }),
  conciliacao: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/conciliacao/${comp}`),
  auditoria: (clienteId, de, ate) => api.get(`/contabil/clientes/${clienteId}/auditoria`, { params: { de, ate } }),
  auditoriaCsv: (clienteId, de, ate) => api.get(`/contabil/clientes/${clienteId}/auditoria.csv`, { params: { de, ate }, responseType: 'blob' }),
  ecd: (clienteId, ano) => api.get(`/contabil/clientes/${clienteId}/ecd/${ano}`),
  ecdTxt: (clienteId, ano) => api.get(`/contabil/clientes/${clienteId}/ecd/${ano}.txt`, { responseType: 'blob', timeout: 120000 }),
  salvarNotasComplemento: (clienteId, ano, texto) => api.put(`/contabil/clientes/${clienteId}/notas-complemento/${ano}`, { texto }),
  encerramento: (clienteId, ano) => api.get(`/contabil/clientes/${clienteId}/encerramento/${ano}`),
  encerrar: (clienteId, ano, forcar = false) => api.post(`/contabil/clientes/${clienteId}/encerramento/${ano}`, { forcar_meses_abertos: forcar }),
  reabrirExercicio: (clienteId, ano) => api.post(`/contabil/clientes/${clienteId}/encerramento/${ano}/reabrir`),
  // contabilização automática
  contabilizacaoPainel: (competencia) => api.get('/contabil/contabilizacao/painel', { params: { competencia } }),
  contabilizacaoRodar: (competencia) => api.post('/contabil/contabilizacao/rodar', { competencia }),
  contabilizacaoConfig: (clienteId) => api.get(`/contabil/clientes/${clienteId}/contabilizacao`),
  contabilizacaoLigar: (clienteId, ligada) => api.put(`/contabil/clientes/${clienteId}/contabilizacao`, { ligada }),
  contabilizacaoPrevia: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/contabilizacao/${comp}`),
  contabilizar: (clienteId, comp) => api.post(`/contabil/clientes/${clienteId}/contabilizacao/${comp}`),
  conferencia: (clienteId, comp) => api.get(`/contabil/clientes/${clienteId}/conferencia/${comp}`),
  pendentesConferir: (clienteId, competencia) => api.get(`/contabil/clientes/${clienteId}/conferir`, { params: { competencia } }),
  reclassificar: (lancamentoId, conta, lembrar = true) => api.post(`/contabil/lancamentos/${lancamentoId}/reclassificar`, { conta, lembrar }),
  // títulos
  painelMes: (competencia) => api.get('/contabil/painel-mes', { params: { competencia } }),
  titulos: (clienteId, params = {}) => api.get(`/contabil/clientes/${clienteId}/titulos`, { params }),
  criarTitulo: (clienteId, dados) => api.post(`/contabil/clientes/${clienteId}/titulos`, dados),
  baixarTitulo: (tid, dados) => api.post(`/contabil/titulos/${tid}/baixar`, dados),
  cancelarTitulo: (tid) => api.post(`/contabil/titulos/${tid}/cancelar`),
  vencimentoTitulo: (tid, vencimento) => api.put(`/contabil/titulos/${tid}/vencimento`, { vencimento }),
};

// Ficha da empresa, mapa de integração e participantes (13/10/2026).
export const empresaApi = {
  ficha: (id) => api.get(`/empresa/${id}/ficha`),
  salvarDados: (id, dados) => api.put(`/empresa/${id}/dados`, dados),
  definirRegime: (id, dados) => api.put(`/empresa/${id}/regime`, dados),
  definirParametros: (id, modulo, valores, vigencia_inicio) => api.put(`/empresa/${id}/parametros/${modulo}`, { valores, vigencia_inicio }),
  estabelecimentos: { listar: (id) => api.get(`/empresa/${id}/estabelecimentos`), criar: (id, d) => api.post(`/empresa/${id}/estabelecimentos`, d), editar: (rid, d) => api.put(`/empresa/estabelecimentos/${rid}`, d), remover: (rid) => api.delete(`/empresa/estabelecimentos/${rid}`) },
  responsaveis: { listar: (id) => api.get(`/empresa/${id}/responsaveis`), criar: (id, d) => api.post(`/empresa/${id}/responsaveis`, d), editar: (rid, d) => api.put(`/empresa/responsaveis/${rid}`, d), remover: (rid) => api.delete(`/empresa/responsaveis/${rid}`) },
  contasBancarias: { listar: (id) => api.get(`/empresa/${id}/contas-bancarias`), criar: (id, d) => api.post(`/empresa/${id}/contas-bancarias`, d), editar: (rid, d) => api.put(`/empresa/contas-bancarias/${rid}`, d), remover: (rid) => api.delete(`/empresa/contas-bancarias/${rid}`) },
  sincronizarContasBancarias: (id) => api.post(`/empresa/${id}/contas-bancarias/sincronizar`),
  mapa: (id) => api.get(`/empresa/${id}/mapa`),
  definirMapa: (id, papel, conta) => api.put(`/empresa/${id}/mapa`, { papel, conta }),
  mapaEscritorio: () => api.get('/empresa/mapa/escritorio'),
  definirMapaEscritorio: (papel, conta) => api.put('/empresa/mapa/escritorio', { papel, conta }),
  participantes: (id, params = {}) => api.get(`/empresa/${id}/participantes`, { params }),
  criarParticipante: (id, d) => api.post(`/empresa/${id}/participantes`, d),
  editarParticipante: (pid, d) => api.put(`/empresa/participantes/${pid}`, d),
  removerParticipante: (pid) => api.delete(`/empresa/participantes/${pid}`),
  sincronizarParticipantes: (id) => api.post(`/empresa/${id}/participantes/sincronizar`),
};

export const balancetesApi = {
  painel: (competencia) => api.get('/balancetes/painel', { params: { competencia } }),
  // Matriz cliente × meses (quadradinhos). params: { ate?, meses?, mei? }
  matriz: (params = {}) => api.get('/balancetes/matriz', { params }),
  validacaoDrive: (params = {}) => api.get('/balancetes/validacao-drive', { params }),
  bancada: () => api.get('/balancetes/bancada'),
  bancadaEmpresas: () => api.get('/balancetes/bancada/empresas'),
  bancadaExecucoes: (params = {}) => api.get('/balancetes/bancada/execucoes', { params }),
  bancadaExecucao: (id) => api.get(`/balancetes/bancada/execucoes/${id}`),
  bancadaDisparar: (job, dados = {}) => api.post(`/balancetes/bancada/${job}`, dados),
  bancadaTxt: (id) => api.get(`/balancetes/bancada/execucoes/${id}/txt`, { responseType: 'blob' }),
  bancadaPdf: (id) => api.get(`/balancetes/bancada/execucoes/${id}/pdf`, { responseType: 'blob', timeout: 120000 }),
  competencia: (clienteId, comp) => api.get(`/balancetes/clientes/${clienteId}/competencias/${comp}`),
  classificar: (clienteId, comp) => api.post(`/balancetes/clientes/${clienteId}/competencias/${comp}/classificar`),
  enviarExtrato: (clienteId, arquivo) => {
    const fd = new FormData(); fd.append('arquivo', arquivo);
    return api.post(`/balancetes/clientes/${clienteId}/extratos`, fd, { timeout: 300000 });
  },
  arquivoExtrato: (id) => api.get(`/balancetes/extratos/${id}/arquivo`, { responseType: 'blob' }),
  apagarExtrato: (id) => api.delete(`/balancetes/extratos/${id}`),
  contaBanco: (id, conta) => api.put(`/balancetes/extratos/${id}/conta-banco`, { conta }),
  revisar: (id, payload) => api.patch(`/balancetes/lancamentos/${id}`, payload),
  regras: (clienteId) => api.get(`/balancetes/clientes/${clienteId}/regras`),
  // Aprende com o razão do Domínio (PDF). → { gabarito, regras, ambiguas, reclassificados, ... }
  aprenderRazao: (clienteId, arquivo) => {
    const fd = new FormData(); fd.append('arquivo', arquivo);
    return api.post(`/balancetes/clientes/${clienteId}/razao`, fd, { timeout: 300000 });
  },
  apagarRegra: (id) => api.delete(`/balancetes/regras/${id}`),
  saldoZero: (clienteId, comp) => api.post(`/balancetes/clientes/${clienteId}/competencias/${comp}/saldo-zero`),
  balanceteAnterior: (clienteId, comp, arquivo) => {
    const fd = new FormData(); fd.append('arquivo', arquivo);
    return api.post(`/balancetes/clientes/${clienteId}/competencias/${comp}/balancete-anterior`, fd, { timeout: 300000 });
  },
  txt: (clienteId, comp) => api.get(`/balancetes/clientes/${clienteId}/competencias/${comp}/txt`, { responseType: 'blob' }),
  pdf: (clienteId, comp) => api.get(`/balancetes/clientes/${clienteId}/competencias/${comp}/pdf`, { responseType: 'blob', timeout: 120000 }),
  fechar: (clienteId, comp, semMovimento = false) => api.post(`/balancetes/clientes/${clienteId}/competencias/${comp}/fechar`, { sem_movimento: !!semMovimento }),
  reabrir: (clienteId, comp) => api.post(`/balancetes/clientes/${clienteId}/competencias/${comp}/reabrir`),
  // uso=true: no plano Marçal devolve só as contas de uso (subcontas, papéis, o que já tem movimento).
  plano: (clienteId, uso = false) => api.get(`/balancetes/clientes/${clienteId}/plano`, { params: uso ? { uso: 1 } : {} }),
  importarPlano: (arquivo, clienteId = 0) => {
    const fd = new FormData(); fd.append('arquivo', arquivo); fd.append('cliente_id', String(clienteId || 0));
    return api.post('/balancetes/plano/importar', fd);
  },
  drivePendentes: () => api.get('/balancetes/drive/pendentes'),
  // Lê AGORA os extratos do Drive de um cliente (segundo plano). 202 { arquivos: [nomes] }
  driveCliente: (clienteId) => api.post(`/balancetes/clientes/${clienteId}/drive`),
  driveRodar: (limite = 10) => api.post('/balancetes/drive/rodar', { limite }),
};

// João · Prestação de contas COSEMS (27/09/2026) — Anexo I quadrimestral. quad = 'AAAA-1|2|3'.
export const prestacaoApi = {
  cosems: () => api.get('/prestacao/cosems'),
  montar: (clienteId, quad) => api.get(`/prestacao/clientes/${clienteId}/quad/${quad}`),
  classificar: (clienteId, quad, refazer = false) => api.post(`/prestacao/clientes/${clienteId}/quad/${quad}/classificar`, { refazer }, { timeout: 300000 }),
  aceitar: (clienteId, quad, ids = null) => api.post(`/prestacao/clientes/${clienteId}/quad/${quad}/aceitar`, { ids }),
  ajustar: (clienteId, quad, payload) => api.put(`/prestacao/clientes/${clienteId}/quad/${quad}`, payload),
  config: (clienteId, payload) => api.put(`/prestacao/clientes/${clienteId}/config`, payload),
  modelo: (clienteId, arquivo) => { const fd = new FormData(); fd.append('arquivo', arquivo); return api.post(`/prestacao/clientes/${clienteId}/modelo`, fd, { timeout: 120000 }); },
  revisar: (lancamentoId, payload) => api.patch(`/prestacao/lancamentos/${lancamentoId}`, payload),
  regras: (clienteId) => api.get(`/prestacao/clientes/${clienteId}/regras`),
  apagarRegra: (id) => api.delete(`/prestacao/regras/${id}`),
  planilha: (clienteId, quad) => api.get(`/prestacao/clientes/${clienteId}/quad/${quad}/planilha`, { responseType: 'blob', timeout: 120000 }),
};

export const relatoriosContabeisApi = {
  // params: { periodo, ano, mes?, trimestre?, regime?, incluirInativas? }
  // periodo: 'ultimos12' (visão da tela — janela rolante; ano+mes são o ÚLTIMO mês dela,
  // devolve serie[12] + trimestres[] + limites) | 'mensal' | 'trimestral' | 'anual'.
  montar: (params = {}) => api.get('/relatorios-contabeis', { params }),
  // Prévia do relatório INDIVIDUAL de uma empresa (as 4 seções + bloqueios).
  previaCliente: (clienteId, params = {}) => api.get(`/relatorios-contabeis/cliente/${clienteId}`, { params }),
  // Enfileira o PDF do relatório da empresa. 202 {id} → polling em documentosApi.status(id).
  // 422 quando o guard anti-zero barra (período sem apuração / mês faltando).
  gerarPdfCliente: (clienteId, payload) => api.post(`/relatorios-contabeis/cliente/${clienteId}/pdf`, payload),
  // Dashboard de FECHAMENTO (1 página por empresa/trimestre — o documento que o
  // escritório confere e depois mostra ao cliente).
  // params: { ano, trimestre } → prévia read-only + bloqueios[] (parâmetro negociado
  // não cadastrado, trimestre sem apuração, planilha em layout desconhecido).
  // params: { periodo, ano, mes, trimestre } — o dashboard sai nos três períodos.
  previaDashboard: (clienteId, params = {}) => api.get(`/relatorios-contabeis/cliente/${clienteId}/dashboard`, { params }),
  // Reflexos das alterações: o fechamento ANTERIOR, que só o escritório sabe.
  lerReflexos: (clienteId, params = {}) => api.get(`/relatorios-contabeis/cliente/${clienteId}/dashboard/reflexos`, { params }),
  salvarReflexos: (clienteId, payload) => api.put(`/relatorios-contabeis/cliente/${clienteId}/dashboard/reflexos`, payload),
  apagarReflexos: (clienteId, params = {}) => api.delete(`/relatorios-contabeis/cliente/${clienteId}/dashboard/reflexos`, { params }),
  // payload: { ano, trimestre }. 202 {id} → polling em documentosApi.status(id).
  // 422 quando há bloqueio — dashboard sem base não vira PDF.
  gerarPdfDashboard: (clienteId, payload) => api.post(`/relatorios-contabeis/cliente/${clienteId}/dashboard/pdf`, payload),
};

// EFD-Reinf — serviços tomados (notas que o cliente RECEBEU pelo Portal Nacional).
// READ-ONLY: as duas rotas só leem o que o sync do ADN já trouxe. Não transmitem.
// EFD-Contribuições: lista da carteira (Presumido + Real) e geração do SPED a
// partir do fechamento. Nada assina nem transmite: o arquivo vai pro PVA.
export const efdContribuicoesApi = {
  carteira: (params = {}) => api.get('/efd-contribuicoes/carteira', { params }),
  conferencia: (clienteId, params = {}) => api.get(`/efd-contribuicoes/${clienteId}/conferencia`, { params }),
  gerar: (clienteId, payload) => api.post(`/efd-contribuicoes/${clienteId}/gerar`, payload),
};

export const reinfApi = {
  // params: { ano, mes } — carteira Lucro Real + Presumido na competência.
  // Sem ano/mes, o backend usa o último mês FECHADO.
  painel: (params = {}) => api.get('/reinf/painel', { params }),
  // A revisão das configurações de envio (envs, certificado, responsável, o que só
  // a Receita responde). Read-only e sem custo — pode ser aberta à vontade.
  prontidao: (params = {}) => api.get('/reinf/prontidao', { params }),
  // params: { cliente_id, ano, mes } — detalhe nota a nota + avisos de conferência.
  competencia: (params = {}) => api.get('/reinf/competencia', { params }),
  // Prestadores da competência com a natureza do rendimento definida ou sugerida.
  prestadores: (params = {}) => api.get('/reinf/prestadores', { params }),
  // Tabela 01 (Grupo 15) inteira — as opções que o operador pode escolher.
  naturezas: () => api.get('/reinf/naturezas'),
  // Grava a natureza de um prestador (vale para as competências seguintes).
  definirNatureza: (payload) => api.post('/reinf/natureza', payload),
  removerNatureza: (params = {}) => api.delete('/reinf/natureza', { params }),
  // PRÉVIA dos eventos da competência (R-4020 + R-4099). Não transmite nada.
  eventos: (params = {}) => api.get('/reinf/eventos', { params }),
  // Conferência das retenções no layout da planilha do escritório.
  conferencia: (params = {}) => api.get('/reinf/conferencia', { params }),
  // Aprovação: o passo humano entre conferir e transmitir. A aprovação vale para
  // os NÚMEROS conferidos — se a conferência mudar, ela vence.
  aprovacao: (params = {}) => api.get('/reinf/aprovacao', { params }),
  aprovar: (payload) => api.post('/reinf/aprovar', payload),
  revogarAprovacao: (payload) => api.post('/reinf/revogar-aprovacao', payload),
  aprovados: (params = {}) => api.get('/reinf/aprovados', { params }),
  // A mesma conferência em planilha. Abre em aba nova, então o token vai na query
  // (o `autenticado` aceita ?token= justamente para downloads).
  conferenciaCsvUrl: ({ cliente_id, ano, mes }) =>
    `${API_BASE}/reinf/conferencia.csv?cliente_id=${cliente_id}&ano=${ano}&mes=${mes}`
    + `&token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
  // Relatório das notas com retenção (número, prestador, CNPJ, total, retenções,
  // líquido). Sem cliente_id = a carteira inteira. Read-only.
  relatorioRetencoes: (params = {}) => api.get('/reinf/relatorio-retencoes', { params }),
  relatorioRetencoesUrl: ({ cliente_id, ano, mes }, formato = 'pdf') =>
    `${API_BASE}/reinf/relatorio-retencoes.${formato}?ano=${ano}&mes=${mes}`
    + (cliente_id ? `&cliente_id=${cliente_id}` : '')
    + `&token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
  // Envio simplificado: a competência inteira nota a nota, com a etapa de cada
  // empresa; o POST aprova e leva até a guia as escolhidas (em série, em 2º plano).
  envio: (params = {}) => api.get('/reinf/envio', { params }),
  envioIniciar: (payload) => api.post('/reinf/envio', { confirmar: 'transmitir', ...payload }),
  envioStatus: () => api.get('/reinf/envio/status'),
  // PDF de um documento arquivado (guia da Reinf), em aba nova.
  documentoPdfUrl: (id) => `${API_BASE}/documentos/${id}/pdf?token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
  // Reinicia a leitura das notas de UM cliente no Portal Nacional (não é SERPRO,
  // não é bilhetado). Roda em segundo plano; a tela acompanha pelo status.
  relerNotas: (payload) => api.post('/reinf/reler-notas', { confirmar: 'reler', ...payload }),
  relerNotasStatus: (params = {}) => api.get('/reinf/reler-notas/status', { params }),
  // Relê a CARTEIRA inteira da competência, uma empresa por vez, com relatório.
  relerCarteiraPrevia: (params = {}) => api.get('/reinf/reler-carteira/previa', { params }),
  relerCarteira: (payload) => api.post('/reinf/reler-carteira', { confirmar: 'reler', ...payload }),
  relerCarteiraStatus: () => api.get('/reinf/reler-carteira/status'),
  // Prévia da transmissão: ambiente, travas e o que ainda barra. Não envia nada.
  transmissao: (params = {}) => api.get('/reinf/transmissao', { params }),
  // ATO FISCAL — envia os eventos prontos à Receita.
  transmitir: (payload) => api.post('/reinf/transmitir', { confirmar: 'transmitir', ...payload }),
  // Traz o resultado do processamento e grava os recibos.
  consultarLote: (payload) => api.post('/reinf/consultar-lote', payload),
  // ATO FISCAL — fecha a competência (R-4099). É o que faz a Receita totalizar e
  // levar o débito à DCTFWeb. Só passa com todos os R-4020 já com recibo.
  fechar: (payload) => api.post('/reinf/fechar', { confirmar: 'transmitir', ...payload }),
  // ATO FISCAL — encerra e transmite a DCTFWeb, de onde a guia sai.
  encerrarDctfweb: (payload) => api.post('/reinf/dctfweb/encerrar', { confirmar: 'encerrar', ...payload }),
  dctfweb: (params = {}) => api.get('/reinf/dctfweb', { params }),
  guia: (params = {}) => api.get('/reinf/dctfweb/guia', { params }),
  // O recibo prova a ENTREGA; a guia prova o que se paga. São dois documentos, e
  // o operador precisa dos dois — quem só guarda a guia fica sem comprovante da
  // declaração.
  recibo: (params = {}) => api.get('/reinf/dctfweb/recibo', { params }),
  // Validação read-only: confronta o que as notas retiveram, o que o evento
  // declararia hoje e o que já foi declarado. Zero SERPRO, zero escrita.
  validacao: (params = {}) => api.get('/reinf/validacao', { params }),
  // Prévia da correção: por série, quem mudou, quem é novo, quem sumiu e se a
  // série está fechada (precisa reabrir). Read-only.
  retificacao: (params = {}) => api.get('/reinf/retificacao', { params }),
  // Nota fora do Portal Nacional, lançada à mão (entra na conferência como "manual").
  notasManuais: (params = {}) => api.get('/reinf/notas-manuais', { params }),
  salvarNotaManual: (dados, id = null) => (id
    ? api.put(`/reinf/notas-manuais/${id}`, dados)
    : api.post('/reinf/notas-manuais', dados)),
  removerNotaManual: (id, clienteId) => api.delete(`/reinf/notas-manuais/${id}`, { params: { cliente_id: clienteId } }),
  // ATO FISCAL — corrige a competência entregue: reabre, retifica (indRetif=2),
  // inclui, exclui (R-9000) e fecha de novo. Mesmo gate do envio original.
  retificar: (payload) => api.post('/reinf/retificar', { confirmar: 'transmitir', ...payload }),
  // ATO FISCAL — a CORRENTE inteira numa autorização: R-4020 → recibos → R-4099 →
  // recibo → DCTFWeb → guia. Para no primeiro passo que não puder seguir e diz até
  // onde chegou. Repetir é seguro: cada passo é idempotente e ela retoma.
  transmitirTudo: (payload) => api.post('/reinf/transmitir-tudo', { confirmar: 'transmitir', ...payload }),
  // Fila de protocolos aguardando recibo (o monitor de retorno). Read-only.
  monitor: () => api.get('/reinf/monitor'),
  monitorRodar: () => api.post('/reinf/monitor/rodar'),
  // Esteira: relê a carteira e diz o que trava cada empresa. Não aprova, não transmite.
  esteira: (params = {}) => api.get('/reinf/esteira', { params }),
  esteiraRodar: (payload) => api.post('/reinf/esteira', payload),
  esteiraStatus: () => api.get('/reinf/esteira/status'),
};

export const acessoEmpresaApi = {
  painel: () => api.get('/acesso-empresa/painel'),
  empresa: (cnpj) => api.get(`/acesso-empresa/empresa/${String(cnpj).replace(/\D/g, '')}`),
  aprovar: (id) => api.post('/acesso-empresa/aprovar', { id }),
  // Libera os pendentes de UMA empresa de uma vez. `ids` = as linhas que a tela
  // mostrou/confirmou (o lote toca só essas — nunca vaza pra fora do filtro). O
  // backend ainda segura os "confira o papel" (precisa_confirmar), que seguem no
  // clique a clique.
  aprovarEmpresa: (cnpj, ids) => api.post('/acesso-empresa/aprovar-empresa', {
    cnpj: String(cnpj).replace(/\D/g, ''),
    ids: Array.isArray(ids) ? ids : undefined,
  }),
  revogar: (id) => api.post('/acesso-empresa/revogar', { id }),
  // Cadastra o par à mão — pro sócio que a leitura dos contratos não achou.
  adicionar: (payload) => api.post('/acesso-empresa/adicionar', payload),
  // Descoberta automática pelo Quadro de Sócios da Receita (grátis, só precisa
  // do CNPJ). É o caminho que dispensa cadastro par a par.
  descobrir: (opts = {}) => api.post('/acesso-empresa/descobrir', opts),
  grupos: () => api.get('/acesso-empresa/grupos'),
  // Grupos econômicos: empresas ligadas por sócio em comum + liberação cruzada.
  gruposEconomicos: () => api.get('/acesso-empresa/grupos-economicos'),
  liberarGrupo: (payload) => api.post('/acesso-empresa/grupos-economicos/liberar', payload),
  revogarGrupo: (payload) => api.post('/acesso-empresa/grupos-economicos/revogar', payload),
  varrer: (opts = {}) => api.post('/acesso-empresa/varrer', opts),
  varreduraProgresso: () => api.get('/acesso-empresa/varredura-progresso'),
  varreduraReset: () => api.post('/acesso-empresa/varredura-reset'),
  conferirQsa: (opts = {}) => api.post('/acesso-empresa/conferir-qsa', opts),
  // Senha PESSOAL (login por CPF no app). `id` = linha da allow-list — o CPF
  // completo nunca passa pela tela. A senha volta em claro UMA única vez.
  senhaPessoal: (id) => api.post('/acesso-empresa/senha-pessoal', { id }),
};

export const folhaCltApi = {
  empresas: () => api.get('/folha-clt/empresas'),
  empregados: (clienteId) => api.get(`/folha-clt/clientes/${clienteId}/empregados`),
  importarEsocial: (clienteId, dados) => api.post(`/folha-clt/clientes/${clienteId}/empregados/importar-esocial`, dados),
  puxadaEstado: (clienteId) => api.get(`/folha-clt/clientes/${clienteId}/esocial/puxar`),
  puxar: (clienteId, dados) => api.post(`/folha-clt/clientes/${clienteId}/esocial/puxar`, dados),
  linhaDoTempo: (clienteId, empId) => api.get(`/folha-clt/clientes/${clienteId}/empregados/${empId}/esocial/linha-do-tempo`),
  xmlEsocial: (clienteId, xid) => api.get(`/folha-clt/clientes/${clienteId}/esocial/xml/${xid}`),
  medias: (clienteId, empId) => api.get(`/folha-clt/clientes/${clienteId}/empregados/${empId}/medias`),
  mediasLancar: (clienteId, empId, dados) => api.put(`/folha-clt/clientes/${clienteId}/empregados/${empId}/medias`, dados),
  mediasCalcular: (clienteId, empId, dados) => api.post(`/folha-clt/clientes/${clienteId}/empregados/${empId}/medias/calcular`, dados),
  mediasImportar: (clienteId, dados) => api.post(`/folha-clt/clientes/${clienteId}/medias/importar`, dados),
  mediasRubricas: (clienteId) => api.get(`/folha-clt/clientes/${clienteId}/medias/rubricas`),
  mediasCriarRubrica: (clienteId, dados) => api.post(`/folha-clt/clientes/${clienteId}/medias/rubricas`, dados),
  mediasAjustarRubrica: (clienteId, dados) => api.patch(`/folha-clt/clientes/${clienteId}/medias/rubricas`, dados),
  puxadaCadastro: (clienteId, dados) => api.post(`/folha-clt/clientes/${clienteId}/esocial/puxar/cadastro`, dados),
  complementar: (clienteId, empId, complemento) => api.patch(`/folha-clt/clientes/${clienteId}/empregados/${empId}/complemento`, { complemento }),
  simular: (clienteId, empId, dados) => api.post(`/folha-clt/clientes/${clienteId}/empregados/${empId}/simular`, dados),
  empresa: (cid) => api.get(`/folha-clt/clientes/${cid}/empresa`),
  salvarEmpresa: (cid, dados) => api.put(`/folha-clt/clientes/${cid}/empresa`, dados),
  virada: (cid, modo) => api.post(`/folha-clt/clientes/${cid}/virada`, { modo }),
  tabela: (cid) => api.get(`/folha-clt/clientes/${cid}/tabela`),
  aprovarTabela: (cid, iniValid) => api.post(`/folha-clt/clientes/${cid}/tabela/aprovar`, { iniValid }),
  competencias: (cid) => api.get(`/folha-clt/clientes/${cid}/competencias`),
  abrirCompetencia: (cid, competencia) => api.post(`/folha-clt/clientes/${cid}/competencias`, { competencia }),
  competencia: (cid, compId) => api.get(`/folha-clt/clientes/${cid}/competencias/${compId}`),
  lancar: (cid, compId, empId, entrada) => api.put(`/folha-clt/clientes/${cid}/competencias/${compId}/lancamentos/${empId}`, { entrada }),
  dataPagamento: (cid, compId, data) => api.put(`/folha-clt/clientes/${cid}/competencias/${compId}/data-pagamento`, { data }),
  calcular: (cid, compId) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/calcular`),
  validar: (cid, compId) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/validar`),
  reabrir: (cid, compId) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/reabrir`),
  eventosCompetencia: (cid, compId, xml) => api.get(`/folha-clt/clientes/${cid}/competencias/${compId}/eventos${xml ? '?xml=1' : ''}`),
  transmitirCompetencia: (cid, compId, confirmar) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/transmitir`, { confirmar }),
  recibos: (cid, compId) => api.get(`/folha-clt/clientes/${cid}/competencias/${compId}/recibos`),
  reciboPdf: (cid, compId, i) => api.get(`/folha-clt/clientes/${cid}/competencias/${compId}/recibos/${i}/pdf`, { responseType: 'blob' }),
  publicar: (cid, compId) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/publicar`),
  ferias: (cid) => api.get(`/folha-clt/clientes/${cid}/ferias`),
  programarFerias: (cid, dados) => api.post(`/folha-clt/clientes/${cid}/ferias`, dados),
  cancelarFerias: (cid, fid) => api.delete(`/folha-clt/clientes/${cid}/ferias/${fid}`),
  admitir: (cid, dados, complemento) => api.post(`/folha-clt/clientes/${cid}/admissao`, { dados, complemento }),
  alterar: (cid, empId, alteracao) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/alteracao`, { alteracao }),
  afastar: (cid, empId, corpo) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/afastamento`, corpo),
  desligar: (cid, empId, dados) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/desligamento`, dados),
  eventos: (cid) => api.get(`/folha-clt/clientes/${cid}/eventos`),
  previaEvento: (cid, eid) => api.get(`/folha-clt/clientes/${cid}/eventos/${eid}/previa`),
  transmitirEvento: (cid, eid, confirmar) => api.post(`/folha-clt/clientes/${cid}/eventos/${eid}/transmitir`, { confirmar }),
  conferirEvento: (cid, eid) => api.post(`/folha-clt/clientes/${cid}/eventos/${eid}/conferir`),
  cancelarEvento: (cid, eid) => api.delete(`/folha-clt/clientes/${cid}/eventos/${eid}`),
  pedido: (chamadoId) => api.get(`/folha-clt/pedido/${chamadoId}`),
  termoPdf: (cid, eid) => api.get(`/folha-clt/clientes/${cid}/eventos/${eid}/termo.pdf`, { responseType: 'blob' }),
  // admissão completa e SST
  preliminar: (cid, empId) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/preliminar`),
  alterarCadastro: (cid, empId, alteracao) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/cadastro`, { alteracao }),
  prorrogar: (cid, empId, novaDtTerm, dtAlteracao) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/prorrogacao`, { nova_dt_term: novaDtTerm, dt_alteracao: dtAlteracao || null }),
  aso: (cid, empId, aso) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/aso`, { aso }),
  condicoes: (cid, empId, condicoes) => api.post(`/folha-clt/clientes/${cid}/empregados/${empId}/condicoes`, { condicoes }),
  papeis: (cid, empId) => api.get(`/folha-clt/clientes/${cid}/empregados/${empId}/papeis`),
  papelPdf: (cid, empId, tipo) => api.get(`/folha-clt/clientes/${cid}/empregados/${empId}/papeis/${tipo}.pdf`, { responseType: 'blob' }),
  // convenção coletiva
  convencoes: (cid) => api.get(`/folha-clt/clientes/${cid}/convencoes`),
  salvarConvencao: (cid, dados, id) => (id ? api.put(`/folha-clt/clientes/${cid}/convencoes/${id}`, dados) : api.post(`/folha-clt/clientes/${cid}/convencoes`, dados)),
  aprovarConvencao: (cid, id) => api.post(`/folha-clt/clientes/${cid}/convencoes/${id}/aprovar`),
  excluirConvencao: (cid, id) => api.delete(`/folha-clt/clientes/${cid}/convencoes/${id}`),
  // conferência com o eSocial, régua da virada e prazos
  conferirEsocial: (cid, compId) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/conferir-esocial`, {}, { timeout: 300000 }),
  regua: (cid) => api.get(`/folha-clt/clientes/${cid}/regua`),
  pendencias: (cid) => api.get(`/folha-clt/pendencias${cid ? `?cliente=${cid}` : ''}`),
  // retificação
  retificar: (cid, compId, motivo) => api.post(`/folha-clt/clientes/${cid}/competencias/${compId}/retificar`, { motivo }),
};

export const prolaboreApi = {
  // Modo paralelo com a Domínio: Extrato Mensal salvo no tipo Texto
  paraleloImportarExtrato: (dados) => api.post('/prolabore/paralelo/importar-extrato', dados),
  esocialPerfilDominio: (dados) => api.post('/prolabore/esocial/perfil-dominio', dados),
  esocialPerfilDoEsocial: (clienteId, dados) => api.post(`/prolabore/clientes/${clienteId}/esocial/perfil-do-esocial`, dados),
  paraleloAderencia: (meses) => api.get(`/prolabore/paralelo/aderencia${meses ? `?meses=${meses}` : ''}`),
  parametros: (vigencia) => api.get(`/prolabore/parametros${vigencia ? `/${vigencia}` : ''}`),
  simular: (dados) => api.post('/prolabore/simular', dados),
  // Sócios (cadastro do operador)
  listarSocios: (clienteId) => api.get(`/prolabore/socios${clienteId ? `?cliente_id=${clienteId}` : ''}`),
  criarSocio: (dados) => api.post('/prolabore/socios', dados),
  atualizarSocio: (id, dados) => api.put(`/prolabore/socios/${id}`, dados),
  // Painel + competências
  painel: (competencia) => api.get(`/prolabore/painel${competencia ? `?competencia=${competencia}` : ''}`),
  // Carteira: fechamento agrupado por empresa (Fator R + compliance + totais)
  carteira: (competencia) => api.get(`/prolabore/carteira${competencia ? `?competencia=${competencia}` : ''}`),
  fatorR: (clienteId) => api.get(`/prolabore/fator-r/${clienteId}`),
  obterCompetencia: (id) => api.get(`/prolabore/competencias/${id}`),
  calcular: (dados) => api.post('/prolabore/competencias/calcular', dados),
  validar: (id) => api.post(`/prolabore/competencias/${id}/validar`),
  ratificar: (id, dados) => api.post(`/prolabore/competencias/${id}/ratificar`, dados),
  // Quick Win #1: comparativo com mês anterior + alerta de mudança
  obterComparativo: (id) => api.get(`/prolabore/competencias/${id}/comparativo-anterior`),
  // Quick Win #2: auto-ratificar com IRRF tradicional
  autoRatificar: (id) => api.post(`/prolabore/competencias/${id}/auto-ratificar`),
  recolher: (id, dados) => api.post(`/prolabore/competencias/${id}/recolher`, dados),
  cancelar: (id, dados) => api.post(`/prolabore/competencias/${id}/cancelar`, dados),
  gerarGuias: (id, dados) => api.post(`/prolabore/competencias/${id}/gerar-guias`, dados),
  // Auto-piloto (Passo 1): triagem limpo × exceção (read-only) + auto-validação gated
  autoClassificar: (competencia) => api.get(`/prolabore/auto/classificar?competencia=${competencia}`),
  autoValidar: (competencia) => api.post(`/prolabore/auto/validar?competencia=${competencia}`),
  // Guias em lote (Passo 4): gera as guias de todos os validados do mês
  guiasLoteListar: (competencia) => api.get(`/prolabore/guias/lote?competencia=${competencia}`),
  guiasLoteGerar: (competencia) => api.post(`/prolabore/guias/lote?competencia=${competencia}`),
  // eSocial (GATED): preview monta+assina+lote sem transmitir; transmitir exige confirmar:'transmitir' + travas
  esocialPreview: (id) => api.get(`/prolabore/competencias/${id}/esocial-preview`),
  esocialTransmitir: (id, dados) => api.post(`/prolabore/competencias/${id}/esocial-transmitir`, dados),
  esocialConsultarLote: (protocolo) => api.get(`/prolabore/esocial/lote/${protocolo}`),
  registrarTransmissao: (id, dados) => api.post(`/prolabore/competencias/${id}/registrar-transmissao`, dados),
  // Puxa o DARF da DCTFWeb das competências já transmitidas e publica no app (Etapa B).
  // Disparo manual (não bloqueado pelo kill-switch do cron). Idempotente.
  autoTransmitirRodar: () => api.post(`/prolabore/auto-transmitir/rodar`, {}),
  // Botão "Transmitir" (layout simples): roda TODO o pipeline de UMA competência
  // (auto-cura → cadastro → folha → DCTFWeb → guia) e volta {estado, erros, gated, resumo}.
  pipelineTransmitir: (id) => api.post(`/prolabore/competencias/${id}/pipeline`, {}),
  // Estado do pipeline (semáforo + 4 etapas) — read-only, pra carregar/atualizar a linha.
  pipelineEstado: (id) => api.get(`/prolabore/competencias/${id}/pipeline-estado`),
  // Amarração POR EMPRESA: transmite todos os sócios do mês juntos (UM fechamento).
  pipelineEmpresaTransmitir: (clienteId, competencia) => api.post(`/prolabore/empresas/${clienteId}/pipeline?competencia=${competencia}`, {}),
  pipelineEmpresaEstado: (clienteId, competencia) => api.get(`/prolabore/empresas/${clienteId}/pipeline-estado?competencia=${competencia}`),
  // Holerite (demonstrativo) / recibo de pró-labore em PDF (padrão Domínio) — competência fechada.
  documentoProlabore: (id, tipo) => api.get(`/prolabore/competencias/${id}/documento/${tipo}`, { responseType: 'blob' }),
  // Puxa a guia DIRECIONADA de UMA competência (rápida — não varre a carteira).
  puxarGuiaCompetencia: (id) => api.post(`/prolabore/competencias/${id}/puxar-guia`, {}),
  // Encerra + transmite a DCTFWeb do pró-labore (GATED). dados: { confirmar:'ENCERRAR', retificadora?, forcar? }
  encerrarDctf: (id, dados) => api.post(`/prolabore/competencias/${id}/encerrar-dctf`, dados),
  // Guias de pró-labore publicadas de um cliente (cockpit) + download do PDF.
  guiasProlaboreListar: (clienteId) => api.get(`/prolabore/clientes/${clienteId}/guias`),
  baixarGuiaProlabore: (id, clienteId) => api.get(`/prolabore/guias/${id}/pdf?cliente_id=${clienteId}`, { responseType: 'blob' }),
  // Data do pagamento do pró-labore (S-1210 dtPgto, regime de caixa) — operador informa.
  definirDataPagamento: (id, data) => api.post(`/prolabore/competencias/${id}/data-pagamento`, { data }),
  // Cadastro inicial no eSocial (empresa "sem movimento" começando pró-labore):
  // S-1005 + S-1020 (tabelas) + S-2300 (sócio). Preview read-only; transmitir GATED.
  cadastroInicialPreview: (clienteId) => api.get(`/prolabore/clientes/${clienteId}/cadastro-inicial/preview`),
  cadastroInicialTransmitir: (clienteId, dados) => api.post(`/prolabore/clientes/${clienteId}/cadastro-inicial/transmitir`, dados),
  // Migração eSocial → Cockpit (descoberta de sócios com pró-labore). Async (varredura
  // pesada em background) + polling do status. gravar=false = prévia (não grava).
  migracaoIniciar: (body) => api.post('/prolabore/migracao/varrer-async', body || {}),
  migracaoStatus: () => api.get('/prolabore/migracao/status'),
  // Frente 1: puxa rubrica (S-1010)/lotação (S-1020)/nascimento+codCateg dos sócios (S-2300)
  // do eSocial do cliente. READ-ONLY + grava config local. body opcional:
  // { ambiente?, escolha?:{rubrica?:{codRubr,ideTabRubr}, lotacao?:{codLotacao}}, maxEventos? }
  esocialPuxarConfig: (clienteId, body) => api.post(`/prolabore/clientes/${clienteId}/esocial-puxar-config`, body || {}),
  // Frente 3: puxa o VALOR do pró-labore por sócio (S-1200) do eSocial, casa por CPF.
  // gravar:false (default) = preview/dry-run; gravar:true grava socios.prolabore_mensal
  // (origem esocial). NÃO transmite, NÃO fecha competência. Anti-chute (sem rubrica não
  // valora; rubrica ausente/valores divergentes devolvem candidatos, não decidem).
  // body: { competencia:'YYYY-MM', gravar?:bool, ambiente?, maxEventos? }
  esocialPuxarProLabore: (clienteId, body) => api.post(`/prolabore/clientes/${clienteId}/esocial-puxar-prolabore`, body || {}),
  // Prontidão eSocial (read-only): estado das travas de envio + o que falta "ligar
  // de verdade" + a demanda pronta pro Thiago. Não transmite, não muda env.
  esocialProntidao: () => api.get('/prolabore/esocial/prontidao'),
  // Onboarding societário: escopo da carteira (read-only, barato — só conta docs
  // no Drive + sócios cadastrados); analisar (dry-run que baixa+lê o contrato) e
  // aplicar (cadastra os sócios, pró-labore em branco) por empresa.
  // body: { aplicar?:bool, puxar_config?:bool, ambiente? }
  onboardingCarteira: (todos) => api.get(`/prolabore/onboarding-societario/carteira${todos ? '?todos=1' : ''}`),
  onboardingAnalisar: (clienteId, body) => api.post(`/prolabore/clientes/${clienteId}/onboarding-societario/analisar`, body || {}),
};

// === CHAMADOS: painel da TV + metas de SLA por prioridade (2026-10-07) ===
export const chamadosApi = {
  tv: (casa) => api.get('/chamados/tv', { params: casa ? { casa } : {} }),
  sla: () => api.get('/chamados/sla'),
  definirSla: (prioridade, body) => api.put(`/chamados/sla/${prioridade}`, body || {}),
  definirPrioridade: (id, prioridade) => api.post(`/chamados/${id}/status`, { prioridade }),
};

// === CENTRAL DE ATENDIMENTO: distribuição por carga, tempos, métricas, TV (2026-10-21) ===
// [Pablo 2026-10-26] Tela da Onésia: clientes novos da NivelOn e o banco de mensagens.
export const primeiroContatoApi = {
  painel: () => api.get('/primeiro-contato/painel'),
  enviar: (id, texto, telefone) => api.post(`/primeiro-contato/novos/${encodeURIComponent(id)}/enviar`, { texto, telefone }),
  marcar: (id, acao) => api.post(`/primeiro-contato/novos/${encodeURIComponent(id)}/marcar`, { acao }),
  mensagens: () => api.get('/primeiro-contato/mensagens'),
  criarMensagem: (titulo, texto) => api.post('/primeiro-contato/mensagens', { titulo, texto }),
  editarMensagem: (id, titulo, texto) => api.put(`/primeiro-contato/mensagens/${id}`, { titulo, texto }),
  moverMensagem: (id, direcao) => api.post(`/primeiro-contato/mensagens/${id}/mover`, { direcao }),
  apagarMensagem: (id) => api.delete(`/primeiro-contato/mensagens/${id}`),
};

export const centralApi = {
  agora: () => api.get('/atendimento-central/agora'),
  filaDaPessoa: (pessoa) => api.get('/atendimento-central/pessoa-fila', { params: { pessoa } }),
  metricas: (dias, empresa) => api.get('/atendimento-central/metricas', { params: { dias, ...(empresa ? { empresa } : {}) } }),
  distribuicao: () => api.get('/atendimento-central/distribuicao'),
  simular: (categoria, empresa) => api.get('/atendimento-central/simular', { params: { categoria, empresa } }),
  definirEquipe: (pessoa, atende) => api.put('/atendimento-central/equipe', { pessoa, atende }),
  definirPessoa: (pessoa, disponivel, observacao) => api.put('/atendimento-central/pessoa', { pessoa, disponivel, observacao }),
  definirHorario: (pessoa, horario) => api.put('/atendimento-central/pessoa/horario', { pessoa, horario }),
  definirTempo: (categoria, horas) => api.put(`/atendimento-central/tempo/${categoria}`, { horas }),
  repasses: (params) => api.get('/atendimento-central/repasses', { params }),
  repassesProntidao: () => api.get('/atendimento-central/repasses/prontidao'),
  repassesMapa: () => api.get('/atendimento-central/repasses/mapa'),
  repassesIniciar: () => api.post('/atendimento-central/repasses/iniciar', { confirmar: 'começar' }),
  expediente: (ano) => api.get('/atendimento-central/expediente', { params: { ano } }),
  primeiroContato: () => api.get('/atendimento-central/primeiro-contato'),
  salvarPrimeiroContato: (dados) => api.put('/atendimento-central/primeiro-contato', dados),
  adicionarFechamento: (d) => api.post('/atendimento-central/expediente', d),
  removerFechamento: (id) => api.delete(`/atendimento-central/expediente/${id}`),
  sla: () => api.get('/atendimento-central/sla'),
  definirSla: (categoria, d) => api.put(`/atendimento-central/sla/${categoria}`, d),
  alertaTeste: (empresa, tipo) => api.post('/atendimento-central/alerta-teste', { empresa, tipo }),
  carregarFeriados: (ano, curitiba = true) => api.post(`/atendimento-central/expediente/feriados/${ano}`, { curitiba }),
};

// Módulo financeiro (cobrança Pix, rotina da Daniele, DRE). [2026-10-13]
export const financeiroApi = {
  acesso: () => api.get('/financeiro/acesso'),
  config: () => api.get('/financeiro/config'),
  salvarConfig: (d) => api.put('/financeiro/config', d),
  testarBanco: () => api.get('/financeiro/banco/teste'),
  resumo: (competencia) => api.get('/financeiro/resumo', { params: { competencia } }),
  contratos: () => api.get('/financeiro/contratos'),
  criarContrato: (d) => api.post('/financeiro/contratos', d),
  atualizarContrato: (id, d) => api.put(`/financeiro/contratos/${id}`, d),
  cobrarContrato: (id, competencia) => api.post(`/financeiro/contratos/${id}/cobrar`, { competencia }),
  cobrancas: (params) => api.get('/financeiro/cobrancas', { params }),
  gerarCobrancas: (competencia) => api.post('/financeiro/cobrancas/gerar', { competencia }),
  cobranca: (id) => api.get(`/financeiro/cobrancas/${id}`),
  qr: (id) => api.get(`/financeiro/cobrancas/${id}/qr`),
  pagar: (id, d) => api.post(`/financeiro/cobrancas/${id}/pagamento`, d),
  estornar: (id) => api.post(`/financeiro/cobrancas/${id}/estornar`),
  cancelar: (id, motivo) => api.post(`/financeiro/cobrancas/${id}/cancelar`, { motivo }),
  nota: (id) => api.post(`/financeiro/cobrancas/${id}/nota`),
  lancamentos: (params) => api.get('/financeiro/lancamentos', { params }),
  criarLancamento: (d) => api.post('/financeiro/lancamentos', d),
  apagarLancamento: (id) => api.delete(`/financeiro/lancamentos/${id}`),
  dre: (ano) => api.get('/financeiro/dre', { params: { ano } }),
  dreGerencial: (params) => api.get('/financeiro/dre/gerencial', { params }),
  painel: () => api.get('/financeiro/painel'),
  rotina: (competencia) => api.get('/financeiro/rotina', { params: { competencia } }),
  criarTarefa: (d) => api.post('/financeiro/rotina', d),
  atualizarTarefa: (id, d) => api.put(`/financeiro/rotina/${id}`, d),
  apagarTarefa: (id) => api.delete(`/financeiro/rotina/${id}`),
  marcarTarefa: (id, competencia, feito) => api.post(`/financeiro/rotina/${id}/feito`, { competencia, feito }),
  notaDaTarefa: (id, competencia) => api.post(`/financeiro/rotina/${id}/nota`, { competencia }),
  lembretes: () => api.get('/financeiro/lembretes'),
  lote: (competencia) => api.get('/financeiro/lote', { params: { competencia } }),
  revisarLote: (id, d) => api.put(`/financeiro/lote/cobrancas/${id}`, d),
  canalLote: (contratoId, canal) => api.put(`/financeiro/lote/contratos/${contratoId}/canal`, { canal }),
  aprovarLote: (competencia, ids, teste = false, confirmacao = {}) => api.post('/financeiro/lote/aprovar', { competencia, ids, teste, ...confirmacao }),
  contatoLote: (contratoId, d) => api.put(`/financeiro/lote/contratos/${contratoId}/contato`, d),
  duplicidadeLote: (id, d) => api.post(`/financeiro/lote/cobrancas/${id}/duplicidade`, d),
  cancelarNotaLote: (id, d) => api.post(`/financeiro/lote/cobrancas/${id}/cancelar-nota`, d),
  pararLote: (loteId) => api.post(`/financeiro/lote/${loteId}/parar`),
  historicoLote: (competencia) => api.get('/financeiro/lote/historico', { params: { competencia } }),
  whatsappCobranca: () => api.get('/financeiro/whatsapp'),
  ligarWhatsappCobranca: (d) => api.post('/financeiro/whatsapp', d),
  qrWhatsappCobranca: () => api.get('/financeiro/whatsapp/qrcode'),
  prepararTeste: (d) => api.post('/financeiro/lote/teste', d),
};

export default api;
